import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import SectionHeading from './UI/SectionHeading';
import LoadingButton from './UI/LoadingButton';
import { useApp } from '../context/AppContext';

const kits = [
  {
    id: 'kit-1',
    name: "The Global Nomad System",
    subtitle: "For the Indefinite Traveler",
    price: "$285",
    tag: "Best Seller",
    description: "A master-class in modularity. Seven interlocking components designed to transform any standard roller into a mobile headquarters. Crafted from high-density 1680D ballistic nylon.",
    features: ["3x Compression Cubes", "1x Tech Folio", "1x Wash Pouch", "2x Shoe Bags"],
    image: "https://images.unsplash.com/photo-1544816155-12df9643f363?q=80&w=1000&auto=format&fit=crop",
    align: "left"
  },
  {
    id: 'kit-2',
    name: "Sahara Minimalist Kit",
    subtitle: "Fast, Light, Unburdened",
    price: "$145",
    tag: "Limited Run",
    description: "For the 'One-Bag' purist. This core set focuses on essential compartmentalization without adding unnecessary weight. Features our proprietary ultra-light ripstop fabric.",
    features: ["1x Expandable Core Cube", "1x Liquid Shield Bag", "1x Document Sleeve"],
    image: "https://images.unsplash.com/photo-1512331283953-19967202267a?q=80&w=1000&auto=format&fit=crop",
    align: "right"
  }
];

const FeaturedKits = () => {
  const { addToCart } = useApp();

  return (
    <section id="kits" className="py-32 px-6 max-w-7xl mx-auto overflow-hidden">
      <SectionHeading 
        badge="Curated Systems"
        title="Engineered for Order."
        subtitle="We provide cohesive workspace experiences for the modern explorer, prioritizing function over excess."
      />

      <div className="space-y-40">
        {kits.map((kit, index) => (
          <motion.div 
            key={kit.id}
            initial={{ opacity: 0, y: 60 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8 }}
            className={`flex flex-col ${kit.align === 'left' ? 'lg:flex-row' : 'lg:flex-row-reverse'} gap-16 lg:gap-24 items-center`}
          >
            {/* Image Side */}
            <div className="w-full lg:w-3/5 relative group">
              <div className="photo-frame p-4 relative z-10 overflow-hidden">
                <div className="overflow-hidden">
                  <img 
                    src={kit.image} 
                    alt={kit.name} 
                    className="w-full aspect-[16/10] object-cover group-hover:scale-105 transition-transform duration-1000 ease-out"
                  />
                </div>
                <div className="absolute top-8 right-8 bg-cobalt text-white px-4 py-2 font-bold tracking-widest z-20 shadow-xl">
                  {kit.price}
                </div>
              </div>
              <div className={`absolute inset-0 bg-parchment-dark/50 -z-10 translate-x-8 translate-y-8 hidden lg:block`}></div>
            </div>

            {/* Content Side */}
            <div className="w-full lg:w-2/5 space-y-8">
              <div className="space-y-2">
                <h3 className="text-4xl font-bold text-cobalt leading-tight">{kit.name}</h3>
                <p className="font-serif italic text-amber text-xl">{kit.subtitle}</p>
              </div>
              
              <p className="text-ink-light text-lg leading-relaxed font-light">
                {kit.description}
              </p>

              <div className="journal-border p-8 bg-white/60 backdrop-blur-sm relative">
                <div className="absolute top-0 left-0 w-1 h-full bg-amber"></div>
                <h4 className="font-bold uppercase tracking-[0.2em] text-xs mb-6 text-cobalt flex items-center gap-2">
                  <SafeIcon icon={FiIcons.FiList} /> System Inventory
                </h4>
                <ul className="grid grid-cols-1 gap-4">
                  {kit.features.map((feature, i) => (
                    <li key={i} className="flex items-center gap-4 text-sm text-ink-light group">
                      <div className="w-6 h-px bg-cobalt/20 group-hover:w-8 group-hover:bg-amber transition-all"></div>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>

              <LoadingButton 
                onClick={() => addToCart(kit)}
                className="w-full bg-cobalt text-white py-5 uppercase tracking-[0.2em] text-[11px] font-black hover:bg-amber transition-all duration-300 shadow-lg shadow-cobalt/10"
              >
                Acquire This System
              </LoadingButton>
            </div>
          </motion.div>
        ))}
      </div>
    </section>
  );
};

export default FeaturedKits;